import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../utils/api';
import {
  applyTrackingConsent,
  bootstrapAttributionTracking,
  initGoogleAnalytics,
  initMetaPixel,
  initTikTokPixel,
  setGlobalGaMeasurementId,
  trackGaPageView,
  trackMetaEvent,
  trackTikTokPageView
} from '../utils/tracking';
import useConsentPreferences from '../hooks/useConsentPreferences';

// Store loaded pixel IDs to avoid duplicates
const loadedPixels = {
  meta: new Set(),
  gads: false,
  tiktok: false
};

function TrackingPixels({ eventPixels = [] }) {
  const location = useLocation();
  const [settings, setSettings] = useState(null);
  const consent = useConsentPreferences();
  const browserAnalyticsConsent = consent.analytics && consent.browserTracking !== false;
  const browserMarketingConsent = consent.marketing && consent.browserTracking !== false;

  // Load global settings on mount
  useEffect(() => {
    if (!browserAnalyticsConsent && !browserMarketingConsent) {
      setSettings(null);
      return undefined;
    }
    const fetchSettings = async () => {
      try {
        const response = await api.get('/settings');
        setSettings(response.data);
      } catch (error) {
        console.error('Failed to load tracking settings:', error);
      }
    };
    fetchSettings();
    return undefined;
  }, [browserAnalyticsConsent, browserMarketingConsent]);

  useEffect(() => {
    applyTrackingConsent({
      ...consent,
      analytics: browserAnalyticsConsent,
      marketing: browserMarketingConsent
    });
    if (!browserMarketingConsent) {
      loadedPixels.meta.clear();
      loadedPixels.gads = false;
      loadedPixels.tiktok = false;
    }
  }, [consent, browserAnalyticsConsent, browserMarketingConsent]);

  // Capture attribution IDs as early as possible on every route update
  useEffect(() => {
    if (!browserMarketingConsent) return;
    bootstrapAttributionTracking(location.search);
  }, [location.search, browserMarketingConsent]);

  // Initialize Google Analytics
  useEffect(() => {
    const isEnabled = settings?.googleAnalytics?.enabled;
    const measurementId = settings?.googleAnalytics?.measurementId?.trim();

    if (!browserAnalyticsConsent || !isEnabled || !measurementId) {
      setGlobalGaMeasurementId('');
      return;
    }

    initGoogleAnalytics(measurementId, { scope: 'global' });
    console.log('[Tracking] Global Google Analytics initialized:', measurementId);
  }, [settings?.googleAnalytics, browserAnalyticsConsent]);

  // Initialize Google Ads
  useEffect(() => {
    if (!browserMarketingConsent || !settings?.googleAds?.enabled || !settings?.googleAds?.conversionId || loadedPixels.gads) return;

    const conversionId = settings.googleAds.conversionId;
    const conversionLabel = settings.googleAds.conversionLabel;
    initGoogleAnalytics(conversionId, { scope: 'ads', category: 'marketing' });
    if (!window.gtag) return;
    loadedPixels.gads = true;

    window.gtag('config', conversionId);

    // Store conversion info for purchase tracking
    window._gadsConversionId = conversionId;
    window._gadsConversionLabel = conversionLabel;

    console.log('[Tracking] Google Ads initialized:', conversionId);
  }, [settings?.googleAds, browserMarketingConsent]);

  // Initialize Meta Pixel (global + event-specific)
  useEffect(() => {
    if (!browserMarketingConsent) return;
    const pixelsToLoad = [];

    // Global pixel
    if (settings?.globalMetaPixel?.enabled && settings?.globalMetaPixel?.pixelId) {
      pixelsToLoad.push(settings.globalMetaPixel.pixelId);
    }

    // Event-specific pixels
    if (eventPixels && eventPixels.length > 0) {
      eventPixels.forEach(pixel => {
        if (pixel.enabled && pixel.pixelId) {
          pixelsToLoad.push(pixel.pixelId);
        }
      });
    }

    // Only load new pixels
    const newPixels = pixelsToLoad.filter(id => !loadedPixels.meta.has(id));
    if (newPixels.length === 0) return;

    // Initialize each new pixel
    newPixels.forEach(pixelId => {
      initMetaPixel(pixelId, { scope: 'global' });
      loadedPixels.meta.add(pixelId);
      console.log('[Tracking] Meta Pixel initialized:', pixelId);
    });
  }, [settings?.globalMetaPixel, eventPixels, browserMarketingConsent]);

  // Initialize TikTok Pixel
  useEffect(() => {
    if (!browserMarketingConsent || !settings?.tiktokPixel?.enabled || !settings?.tiktokPixel?.pixelId || loadedPixels.tiktok) return;

    const pixelId = settings.tiktokPixel.pixelId;
    loadedPixels.tiktok = true;

    initTikTokPixel(pixelId, { scope: 'global' });
    console.log('[Tracking] TikTok Pixel initialized:', pixelId);
  }, [settings?.tiktokPixel, browserMarketingConsent]);

  // Track page views on route change
  useEffect(() => {
    if (!settings || (!browserAnalyticsConsent && !browserMarketingConsent)) return;

    // Google Analytics page view
    if (browserAnalyticsConsent) {
      trackGaPageView({
        pagePath: location.pathname + location.search,
        pageTitle: document.title
      });
    }

    // Meta Pixel page view (already fired on init, but fire on route changes)
    if (window.fbq) {
      trackMetaEvent('PageView');
    }

    // TikTok page view
    if (window.ttq) {
      trackTikTokPageView();
    }
  }, [location, settings, browserAnalyticsConsent, browserMarketingConsent]);

  // This component doesn't render anything
  return null;
}

export default TrackingPixels;
